import { useState } from 'react';
import type { SurveyConfig, Question } from './types';
import { optionTextsOf, withOptionText, selectLimitOf, questionImages, TEXT_VALIDATIONS, matrixTextRowValidationOf, matrixColTextsOf, withMatrixColText, allowedOptionsOf, pruneOptionLinkAnswers, matrixLikeAnswered, matrixTextColumnsOf, matrixTextCellValue, matrixTextCellValidationOf } from './types';
import QuestionInput, { RankingInput } from '../runner/QuestionInput';
import DatePickerInput from '../runner/DatePickerInput';
import AutoGrowTextarea from '../runner/AutoGrowTextarea';
import { computeVisibleQuestions, computeJumpPath } from '../../lib/display-logic-bridge';
import ImageLightbox from '../ImageLightbox';
import SignaturePad from '../runner/SignaturePad';
import RichTextView from './RichTextView';
import { paginateQuestions, displayIndexMap } from './question-utils';

type Props = {
  config: SurveyConfig;
};

export default function PreviewPanel({ config }: Props) {
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [lightboxSrc, setLightboxSrc] = useState<{ src: string; alt: string } | null>(null);
  const [signature, setSignature] = useState<string | null>(null);
  // 分页模式：有 pagebreak 时显示页码切换；无 pagebreak 时保持单页（沿用旧逻辑）
  const pages = paginateQuestions(config.questions);
  const hasPager = pages.length > 1;
  const [pageIndex, setPageIndex] = useState(0);

  const visibleIds = computeVisibleQuestions(config, answers);
  const visibleIdSet = new Set(visibleIds);
  const visible = config.questions.filter((q) => visibleIds.includes(q.id));
  // 0904 起跳题逻辑：path = 实际遍历到的题；预览端按 path 展示
  const { path: jumpPath } = computeJumpPath(config, answers);
  const reachableIds = new Set(jumpPath);
  const reachable = config.questions.filter((q) => reachableIds.has(q.id));

  const preface = config.settings?.preface;
  const [prefaceChecked, setPrefaceChecked] = useState(false);
  const sigOnPreface = !!(config.settings?.signature?.enabled && (config.settings?.signature as any)?.onPreface);
  const prefaceExtraQs = (preface?.questions ?? []) as any[];
  const prefaceQs = [...prefaceExtraQs, ...visible.filter((q) => q.onPreface)];
  const prefaceActive = (!!(preface?.body || preface?.title || preface?.image) || prefaceQs.length > 0 || sigOnPreface) && !prefaceChecked;
  const missingPreface = prefaceQs.filter((q) => q.required && !isAnswered(q, answers[q.id]));
  const canAcknowledge = missingPreface.length === 0;
  const mainQsAll = reachable.filter((q) => !q.onPreface);
  // 当前页题目（仅显示当前页的题；pagebreak 在切片中被消耗掉，不会出现在页内）
  const currentPageQs = hasPager ? (pages[pageIndex] ?? []).filter((q) => mainQsAll.some((mq) => mq.id === q.id)) : mainQsAll;
  // 0907 选项关联：源题切换后，把目标题里已不可见的勾选立即清掉（与填写端 Runner 同规则）
  const setAns = (qid: string, v: any) => setAnswers((a) => {
    const next = { ...a, [qid]: v };
    return pruneOptionLinkAnswers(config, next, new Set(computeVisibleQuestions(config, next)));
  });
  // 0902 选项附属填空
  const setOptText = (qid: string, optValue: string, text: string) => setAnswers((a) => withOptionText(a, qid, optValue, text));
  // 1627 矩阵列附属填空
  const setMatrixColText = (qid: string, row: string, text: string) => setAnswers((a) => withMatrixColText(a, qid, row, text));

  return (
    <div className="preview">
      <div className="preview-head">
        <h1>{config.header.mainTitle || '未命名问卷'}</h1>
        {config.header.subTitle && <p>{config.header.subTitle}</p>}
      </div>

      {prefaceActive ? (
        <div className="preface-card">
          {(preface?.title || preface?.body || preface?.image) && (
            <>
              <h2>{preface?.title ?? '前置须知'}</h2>
              {preface?.image && <img src={preface.image} alt="前置页图片" className="preface-image" />}
              {preface?.body && <pre className="preface-body">{preface.body}</pre>}
            </>
          )}
          {prefaceQs.length > 0 && (
            <ol className="preview-questions" style={{ marginTop: 12 }}>
              {prefaceQs.map((q) => (
                <li key={q.id} className="pq-item">
                  <div className="pq-no">{displayIndexMap(config.questions).get(q.id) ?? 0}</div>
                  <div className="pq-main">
                    <div className="pq-title">
                      <RichTextView value={q.title} />
                      {q.required && <span className="req">*</span>}
                    </div>
                    {q.description && <div className="pq-desc">{q.description}</div>}
                    <PreviewImages images={questionImages(q)} onOpen={(src, alt) => setLightboxSrc({ src, alt })} />
                    {renderQuestionInput(q, answers[q.id], (v) => setAns(q.id, v), (src, alt) => setLightboxSrc({ src, alt }), optionTextsOf(answers, q.id), (ov, t) => setOptText(q.id, ov, t), matrixColTextsOf(answers, q.id), (row, t) => setMatrixColText(q.id, row, t), allowedOptionsOf(q, answers, visibleIdSet))}
                  </div>
                </li>
              ))}
            </ol>
          )}
          {sigOnPreface && (
            <div className="sig-block">
              <h3>签署人签名</h3>
              <SignaturePad mode={config.settings?.signature?.mode ?? 'draw'} value={signature} onChange={setSignature} />
            </div>
          )}
          <label className={`checkbox ${!canAcknowledge ? 'is-disabled' : ''}`}>
            <input type="checkbox" checked={prefaceChecked} disabled={!canAcknowledge} onChange={(e) => e.target.checked && setPrefaceChecked(true)} />
            我已知晓
          </label>
          {!canAcknowledge && <div className="hint hint-bad">请先完成前置页中的所有必答题</div>}
        </div>
      ) : (
        <>
          <ol className="preview-questions">
            {(() => {
              const dmap = displayIndexMap(config.questions);
              return currentPageQs.map((q) => {
                const no = q.type === 'statement' ? '★' : dmap.get(q.id) ?? 0;
                return (
              <li key={q.id} className={`pq-item ${q.type === 'statement' ? ' is-statement' : ''}${q.type === 'pagebreak' ? ' is-pagebreak' : ''}`}>
                <div className="pq-no">{no}</div>
                <div className="pq-main">
                  {q.type === 'statement' ? (
                    <div className="pq-statement-block">
                      <RichTextView value={q.title} variant="statement" />
                      {q.description && <div className="pq-desc">{q.description}</div>}
                      <PreviewImages images={questionImages(q)} onOpen={(src, alt) => setLightboxSrc({ src, alt })} />
                    </div>
                  ) : (
                    <>
                      <div className="pq-title">
                        <RichTextView value={q.title} />
                        {q.required && <span className="req">*</span>}
                      </div>
                      {q.description && <div className="pq-desc">{q.description}</div>}
                      <PreviewImages images={questionImages(q)} onOpen={(src, alt) => setLightboxSrc({ src, alt })} />
                      {renderQuestionInput(
                        q,
                        answers[q.id],
                        (v) => setAns(q.id, v),
                        (src, alt) => setLightboxSrc({ src, alt }),
                        optionTextsOf(answers, q.id),
                        (ov, t) => setOptText(q.id, ov, t),
                        matrixColTextsOf(answers, q.id),
                        (row, t) => setMatrixColText(q.id, row, t),
                        allowedOptionsOf(q, answers, visibleIdSet),
                      )}
                    </>
                  )}
                </div>
              </li>
                );
              });
            })()}
            {currentPageQs.length === 0 && <li className="pq-empty">{hasPager ? `第 ${pageIndex + 1} 页无可见题目` : '无可见题目（条件未满足或均在前置页）'}</li>}
          </ol>

          {hasPager && (
            <div className="runner-pager">
              <button
                type="button"
                className="btn"
                disabled={pageIndex === 0}
                onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
              >
                ← 上一页
              </button>
              <span className="runner-pager-info">第 {pageIndex + 1} 页 / 共 {pages.length} 页</span>
              {pageIndex < pages.length - 1 ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setPageIndex((i) => Math.min(pages.length - 1, i + 1))}
                >
                  下一页 →
                </button>
              ) : (
                <button type="button" className="btn btn-primary" disabled title="预览模式无提交">
                  提交（预览模式无提交）
                </button>
              )}
            </div>
          )}
        </>
      )}

      {!prefaceActive && !sigOnPreface && config.settings?.signature?.enabled && !hasPager && (
        <div className="sig-block">
          <h3>电子签（{config.settings.signature.mode === 'draw' ? '手写' : '上传'}）</h3>
          <SignaturePad
            mode={config.settings.signature.mode ?? 'draw'}
            value={signature}
            onChange={setSignature}
          />
        </div>
      )}

      {lightboxSrc && (
        <ImageLightbox
          src={lightboxSrc.src}
          alt={lightboxSrc.alt}
          onClose={() => setLightboxSrc(null)}
        />
      )}
    </div>
  );
}

function normalizeOptions(q: Question): Array<{ value: string; image?: string; allowText?: boolean; exclusive?: boolean }> {
  return (q.options ?? []).map((o) =>
    typeof o === 'string' ? { value: o } : { value: o.value, image: o.image, allowText: o.allowText, exclusive: o.exclusive },
  );
}

function isAnswered(q: { type?: string; params?: any }, value: any): boolean {
  if (value === undefined || value === null || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  // 矩阵量表 / 矩阵填空（0907 起可多列）/ 矩阵滑动：每行（每格）都答才算已答
  const matrixDone = matrixLikeAnswered(q, value);
  if (matrixDone !== null) return matrixDone;
  if (q.type === 'file') return !!(typeof value === 'object' && (value.dataUrl || value.attachment));
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
}

function renderQuestionInput(
  q: Question,
  value: any,
  onChange: (v: any) => void,
  onImageClick: (src: string, alt: string) => void,
  optionText?: Record<string, string>,
  onOptionText?: (optValue: string, text: string) => void,
  matrixColText?: Record<string, string>,
  onMatrixColText?: (row: string, text: string) => void,
  /** 0907 选项关联：允许展示的选项集合（null = 不限制） */
  allowed?: Set<string> | null,
) {
  const filterOpts = <T extends { value: string }>(opts: T[]): T[] => (allowed ? opts.filter((o) => allowed.has(o.value)) : opts);
  switch (q.type) {
    case 'text': {
      const kind = (q.params as any)?.validation;
      const rule = kind && kind in TEXT_VALIDATIONS ? TEXT_VALIDATIONS[kind as keyof typeof TEXT_VALIDATIONS] : null;
      // 0907 日期校验：日历选择器
      if (kind === 'date') return <DatePickerInput className="pq-input" value={typeof value === 'string' ? value : ''} onChange={onChange} style={{ marginTop: 'var(--space-2)' }} />;
      return (
        <AutoGrowTextarea
          className="pq-input"
          value={value ?? ''}
          placeholder={rule?.hint ?? '请输入内容'}
          maxLength={rule?.maxLength}
          inputMode={rule?.inputMode}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    }
    case 'longtext': {
      const maxLen = (q.params as any)?.maxLen;
      const kind = (q.params as any)?.validation;
      const rule = kind && kind in TEXT_VALIDATIONS ? TEXT_VALIDATIONS[kind as keyof typeof TEXT_VALIDATIONS] : null;
      if (kind === 'date') return <DatePickerInput className="pq-input" value={typeof value === 'string' ? value : ''} onChange={onChange} style={{ marginTop: 'var(--space-2)' }} />;
      return (
        <AutoGrowTextarea
          className="pq-input pq-textarea"
          rows={3}
          maxLength={rule?.maxLength ?? maxLen}
          placeholder={rule?.hint ?? '请输入内容'}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    }
    case 'single': {
      const opts = filterOpts(normalizeOptions(q));
      return (
        <div className="pq-radio-cards">
          {opts.map((o) => {
            const checked = value === o.value;
            return (
              <label key={o.value} className={`pq-radio-card ${checked ? 'is-checked' : ''} ${o.allowText && checked ? 'has-text' : ''}`}>
                <div className="pq-radio-meta">
                  <span className="dot" />
                  <input
                    type="radio"
                    name={`pq-${q.id}`}
                    checked={checked}
                    onChange={() => onChange(o.value)}
                  />
                  <span className="pq-radio-label">{o.value}</span>
                </div>
                {o.image && (
                  <button
                    type="button"
                    className="pq-opt-img-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onImageClick(o.image!, o.value);
                    }}
                    title="点击查看大图"
                    aria-label="点击查看大图"
                  >
                    <img src={o.image} alt={o.value} className="pq-opt-img" />
                  </button>
                )}
                {o.allowText && checked && (
                  <AutoGrowTextarea
                    className="pq-input pq-opt-text"
                    value={optionText?.[o.value] ?? ''}
                    placeholder="请填写"
                    onChange={(e) => onOptionText?.(o.value, e.target.value)}
                  />
                )}
              </label>
            );
          })}
        </div>
      );
    }
    case 'multi': {
      const opts = filterOpts(normalizeOptions(q));
      const arr = Array.isArray(value) ? value : [];
      // 0902 选数限制：达到 maxSelect 后其余未选项不可勾选
      const limit = selectLimitOf(q);
      const atMax = limit?.max != null && arr.length >= limit.max;
      // 0904 多选互斥：勾选互斥项 → 自动取消所有普通项；勾选普通项 → 自动取消所有互斥项
      const exclusiveValues = new Set(opts.filter((o) => o.exclusive === true).map((o) => o.value));
      const toggleExclusive = (cur: string[], optValue: string, checked: boolean): string[] => {
        if (!checked) return cur.filter((x) => x !== optValue);
        if (exclusiveValues.has(optValue)) return [...cur.filter((v) => exclusiveValues.has(v) && v !== optValue), optValue];
        return [...cur.filter((v) => !exclusiveValues.has(v) && v !== optValue), optValue];
      };
      return (
        <div className="pq-radio-cards">
          {opts.map((o) => {
            const checked = arr.includes(o.value);
            const locked = atMax && !checked;
            const lockedByExclusive = !checked && exclusiveValues.size > 0
              ? (exclusiveValues.has(o.value)
                ? arr.some((v) => !exclusiveValues.has(v))
                : arr.some((v) => exclusiveValues.has(v))
              )
              : false;
            return (
              <label key={o.value} className={`pq-radio-card ${checked ? 'is-checked' : ''} ${locked || lockedByExclusive ? 'is-locked' : ''} ${o.allowText && checked ? 'has-text' : ''}`} title={locked ? limit?.maxHint : lockedByExclusive ? '与已选选项互斥' : undefined}>
                <div className="pq-radio-meta">
                  <span className="dot" />
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={locked || lockedByExclusive}
                    onChange={(e) => {
                      const next = toggleExclusive(arr, o.value, e.target.checked);
                      onChange(next);
                    }}
                  />
                  <span className="pq-radio-label">{o.value}</span>
                  {o.exclusive && <span className="exclusive-tag" title="互斥项">互斥</span>}
                </div>
                {o.image && (
                  <button
                    type="button"
                    className="pq-opt-img-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onImageClick(o.image!, o.value);
                    }}
                    title="点击查看大图"
                    aria-label="点击查看大图"
                  >
                    <img src={o.image} alt={o.value} className="pq-opt-img" />
                  </button>
                )}
                {o.allowText && checked && (
                  <AutoGrowTextarea
                    className="pq-input pq-opt-text"
                    value={optionText?.[o.value] ?? ''}
                    placeholder="请填写"
                    onChange={(e) => onOptionText?.(o.value, e.target.value)}
                  />
                )}
              </label>
            );
          })}
          {limit && <div className="select-limit-hint">{[limit.minHint, limit.maxHint].filter(Boolean).join(' · ')}（已选 {arr.length} 项）</div>}
        </div>
      );
    }
    case 'ranking':
      return <RankingInput question={q} value={value} onChange={onChange} allowedOptions={allowed} />;
    case 'matrixslider':
    case 'slider':
      return <QuestionInput question={q} value={value} onChange={onChange} />;
    case 'select': {
      const shown = filterOpts(normalizeOptions(q));
      const selected = shown.find((o) => o.value === value);
      return (
        <div className="select-with-text">
          <div className="select" style={{ maxWidth: 320 }}>
            <select value={selected ? value : ''} onChange={(e) => onChange(e.target.value || undefined)}>
              <option value="">请选择</option>
              {shown.map((o, i: number) => <option key={i} value={o.value}>{o.value}</option>)}
            </select>
          </div>
          {selected?.allowText && (
            <AutoGrowTextarea
              className="pq-input pq-opt-text"
              style={{ maxWidth: 320, marginTop: 0 }}
              value={optionText?.[selected.value] ?? ''}
              placeholder="请填写"
              onChange={(e) => onOptionText?.(selected.value, e.target.value)}
            />
          )}
        </div>
      );
    }
    case 'matrixtext': {
      // 0907 按行独立控件：每行可单独选 text/select；select 行从 rowControls[r].options 取选项
      // 答案统一 { 行: 文本 }（多列老数据不再支持）
      const rows: string[] = (q.params as any)?.rows ?? [];
      const rowControls: Record<string, { kind?: 'text' | 'select'; options?: string[] }> = ((q.params as any)?.rowControls as any) ?? {};
      const map: Record<string, any> = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
      return (
        <div className="matrixtext-wrap">
          <table className="matrixtext-table">
            <tbody>
              {rows.map((r) => {
                const ctrl = rowControls[r] ?? { kind: 'text' };
                const kind = matrixTextRowValidationOf(q, r);
                const rule = kind ? TEXT_VALIDATIONS[kind] : null;
                const cell = map[r] === undefined || map[r] === null ? '' : String(map[r]);
                if (ctrl.kind === 'select') {
                  const opts: string[] = Array.isArray(ctrl.options) ? ctrl.options : [];
                  return (
                    <tr key={r}>
                      <th scope="row" className="matrixtext-row-label">{r}</th>
                      <td>
                        <div className="matrixtext-cell-select">
                          <select value={cell} onChange={(e) => onChange({ ...map, [r]: e.target.value })} aria-label={r}>
                            <option value="">请选择</option>
                            {opts.map((o) => <option key={o} value={o}>{o}</option>)}
                          </select>
                        </div>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={r}>
                    <th scope="row" className="matrixtext-row-label">{r}</th>
                    <td>
                      {kind === 'date' ? (
                        <DatePickerInput className="pq-input" value={cell} onChange={(v) => onChange({ ...map, [r]: v })} ariaLabel={r} />
                      ) : (
                        <AutoGrowTextarea
                          className="pq-input"
                          style={{ marginTop: 0 }}
                          value={cell}
                          placeholder={rule?.hint ?? '请输入'}
                          maxLength={rule?.maxLength}
                          inputMode={rule?.inputMode}
                          onChange={(e) => onChange({ ...map, [r]: e.target.value })}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }
    case 'matrix': {
      const rows: string[] = (q.params as any)?.rows ?? [];
      const cols: string[] = (q.params as any)?.columns ?? [];
      // 1627 矩阵列填空
      const colAllowText: boolean[] = Array.isArray((q.params as any)?.columnAllowText) ? ((q.params as any).columnAllowText as boolean[]) : [];
      const map = value && typeof value === 'object' ? value : {};
      return (
        <div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', minWidth: 320 }}>
              <thead>
                <tr><th></th>{cols.map((c) => <th key={c} style={{ padding: '4px 8px', fontWeight: 500, fontSize: 12 }}>{c}</th>)}</tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r}>
                    <td style={{ padding: '6px 8px', fontSize: 13, whiteSpace: 'nowrap' }}>{r}</td>
                    {cols.map((c) => (
                      <td key={c} style={{ textAlign: 'center', padding: '6px 8px' }}>
                        <input type="radio" name={`pv-${q.id}-${r}`} checked={map[r] === c} onChange={() => onChange({ ...map, [r]: c })} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* 1627 矩阵列填空预览：选中 allowText 列时该行下方显示输入框 */}
          {cols.some((_, i) => colAllowText[i]) && (
            <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
              {rows.map((r) => {
                const selectedCol = map[r];
                const idx = selectedCol ? cols.indexOf(selectedCol) : -1;
                if (idx < 0 || !colAllowText[idx]) return null;
                return (
                  <div key={r} style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 1fr) 2fr', gap: 10, alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: '#68758a' }}>{r} · {selectedCol}</span>
                    <AutoGrowTextarea
                      className="pq-input"
                      style={{ marginTop: 0 }}
                      placeholder="请补充说明"
                      value={matrixColText?.[r] ?? ''}
                      onChange={(e) => onMatrixColText?.(r, e.target.value)}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    }
    case 'file':
      return <div className="pq-input-box" style={{ color: 'var(--text-2)' }}>填写端可上传附件（图片 / PDF）</div>;
    default:
      return null;
  }
}
/** 题干配图（0903 起可多张）：缩略图横排，点击看大图 */
function PreviewImages({ images, onOpen }: { images: string[]; onOpen: (src: string, alt: string) => void }) {
  if (!images.length) return null;
  return (
    <div className="pq-images">
      {images.map((src, i) => (
        <button type="button" key={`${src.slice(0, 32)}-${i}`} onClick={() => onOpen(src, `题图 ${i + 1}`)} title="点击查看大图" aria-label="点击查看大图">
          <img src={src} alt={`题图 ${i + 1}`} />
        </button>
      ))}
    </div>
  );
}
